// =============================================================================
// Admin E-Sign Templates Management Page
// =============================================================================

import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  DocumentTextIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  DocumentDuplicateIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { Card } from '../../../components/ui';
import esignService from '../../../services/esign.service';
import type { SignatureTemplate, SignatureDocType } from '../../../types/esign';

// =============================================================================
// Constants
// =============================================================================

const PAGE_SIZE = 20;

const DOC_TYPE_LABELS: Record<SignatureDocType, string> = {
  NDA: 'NDA',
  SUBSCRIPTION: 'Subscription',
  SIDE_LETTER: 'Side Letter',
  PPM_ACKNOWLEDGMENT: 'PPM Acknowledgment',
  TAX_FORM: 'Tax Form',
  ACCREDITATION: 'Accreditation',
  OTHER: 'Other',
};

const DOC_TYPE_COLORS: Record<SignatureDocType, string> = {
  NDA: 'bg-blue-100 text-blue-800',
  SUBSCRIPTION: 'bg-purple-100 text-purple-800',
  SIDE_LETTER: 'bg-teal-100 text-teal-800',
  PPM_ACKNOWLEDGMENT: 'bg-yellow-100 text-yellow-800',
  TAX_FORM: 'bg-orange-100 text-orange-800',
  ACCREDITATION: 'bg-indigo-100 text-indigo-800',
  OTHER: 'bg-gray-100 text-gray-800',
};

const ALL_DOC_TYPES: SignatureDocType[] = [
  'NDA', 'SUBSCRIPTION', 'SIDE_LETTER', 'PPM_ACKNOWLEDGMENT',
  'TAX_FORM', 'ACCREDITATION', 'OTHER',
];

// =============================================================================
// Delete Confirmation Modal
// =============================================================================

interface DeleteModalProps {
  template: SignatureTemplate;
  onClose: () => void;
  onConfirm: () => void;
  deleting: boolean;
}

function DeleteTemplateModal({ template, onClose, onConfirm, deleting }: DeleteModalProps) {
  const [confirmText, setConfirmText] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-100 rounded-full">
            <TrashIcon className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Delete Template</h3>
        </div>

        <p className="text-sm text-gray-600 mb-2">
          You are about to permanently delete this template:
        </p>
        <p className="text-sm font-medium text-gray-900 mb-4 p-2 bg-gray-50 rounded-lg">
          {template.name}
        </p>
        <p className="text-sm text-red-600 mb-4">
          This action cannot be undone. Any envelopes using this template will not be affected.
        </p>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type <span className="font-bold">proceed</span> below to confirm deletion:
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type 'proceed' to confirm"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            autoFocus
          />
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={confirmText.toLowerCase() !== 'proceed' || deleting}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {deleting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Deleting...
              </>
            ) : (
              'Delete Template'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Component
// =============================================================================

export default function TemplatesPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<SignatureTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>();

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState<SignatureTemplate | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Toggle active state
  const [toggling, setToggling] = useState<string | null>(null);

  // Debounce search input
  useEffect(() => {
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(debounceTimer.current);
  }, [search]);

  // Load templates when filters change
  useEffect(() => {
    loadTemplates();
  }, [typeFilter, statusFilter, debouncedSearch, page]);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await esignService.listTemplates({
        search: debouncedSearch || undefined,
        documentType: typeFilter !== 'all' ? typeFilter : undefined,
        isActive: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
        page,
        pageSize: PAGE_SIZE,
      });
      setTemplates(result.data);
      setTotalCount(result.meta?.pagination?.total ?? result.data.length);
    } catch (err) {
      console.error('Failed to load templates:', err);
      setError(err instanceof Error ? err.message : 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter, debouncedSearch, page]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await esignService.deleteTemplate(deleteTarget.id);
      // Reload current page to refresh counts
      await loadTemplates();
      setDeleteTarget(null);
    } catch (err) {
      console.error('Failed to delete template:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete template');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (template: SignatureTemplate) => {
    setToggling(template.id);
    try {
      const updated = await esignService.updateTemplate(template.id, { isActive: !template.isActive });
      setTemplates(templates.map((t) => (t.id === updated.id ? updated : t)));
    } catch (err) {
      console.error('Failed to toggle template:', err);
      setError(err instanceof Error ? err.message : 'Failed to update template');
    } finally {
      setToggling(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // Loading skeleton
  if (loading && templates.length === 0) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-64 bg-gray-200 rounded" />
        <div className="h-12 bg-gray-200 rounded-lg" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-20 bg-gray-200 rounded-xl" />
          ))}
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-gray-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">E-Sign Templates</h1>
          <p className="mt-1 text-sm text-gray-500">Manage document templates for electronic signatures</p>
        </div>
        <button
          onClick={() => navigate('/admin/esign/templates/new')}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800"
        >
          <PlusIcon className="h-4 w-4" />
          New Template
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <Card padding="md">
          <div className="flex items-center justify-between text-red-700">
            <span className="text-sm">Error: {error}</span>
            <button onClick={() => { setError(null); loadTemplates(); }} className="text-sm font-medium underline hover:no-underline">
              Retry
            </button>
          </div>
        </Card>
      )}

      {/* Search & Filters */}
      <Card padding="md">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search templates..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div className="w-full md:w-44">
            <select
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Types</option>
              {ALL_DOC_TYPES.map((type) => (
                <option key={type} value={type}>{DOC_TYPE_LABELS[type]}</option>
              ))}
            </select>
          </div>
          <div className="w-full md:w-40">
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value as typeof statusFilter); setPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card padding="sm">
          <p className="text-sm text-gray-500">Total Results</p>
          <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
        </Card>
        <Card padding="sm">
          <p className="text-sm text-gray-500">On This Page</p>
          <p className="text-2xl font-bold text-gray-400">{templates.length}</p>
        </Card>
        <Card padding="sm">
          <p className="text-sm text-gray-500">Page</p>
          <p className="text-2xl font-bold text-blue-600">{page} of {totalPages || 1}</p>
        </Card>
        <Card padding="sm">
          <p className="text-sm text-gray-500">Showing</p>
          <p className="text-2xl font-bold text-green-600">
            {templates.length > 0 ? `${(page - 1) * PAGE_SIZE + 1}-${(page - 1) * PAGE_SIZE + templates.length}` : '0'}
          </p>
        </Card>
      </div>

      {/* Templates List */}
      <div className={`space-y-3 ${loading ? 'opacity-50 pointer-events-none' : ''}`}>
        {templates.map((template) => (
          <Card key={template.id} padding="md" hover>
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              {/* Template Info (clickable area) */}
              <Link
                to={`/admin/esign/templates/${template.id}`}
                className="flex items-start gap-4 flex-1 min-w-0"
              >
                <div className="hidden sm:flex w-12 h-12 rounded-xl bg-gray-100 items-center justify-center flex-shrink-0">
                  <DocumentTextIcon className="h-6 w-6 text-gray-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-semibold text-gray-900 truncate">{template.name}</h3>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${DOC_TYPE_COLORS[template.documentType]}`}>
                      {DOC_TYPE_LABELS[template.documentType]}
                    </span>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      template.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {template.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {template.description && (
                    <p className="text-sm text-gray-500 mt-0.5 truncate">{template.description}</p>
                  )}
                  <div className="flex items-center gap-4 mt-1.5 text-xs text-gray-400">
                    <span>{template.pageCount} {template.pageCount === 1 ? 'page' : 'pages'}</span>
                    <span>{(template._count?.fields ?? template.fields?.length ?? 0)} fields</span>
                    <span>{formatFileSize(template.sourceDocumentSize)}</span>
                    <span>Created {formatDate(template.createdAt)}</span>
                  </div>
                </div>
              </Link>

              {/* Actions */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Toggle Active/Inactive */}
                <button
                  onClick={(e) => { e.preventDefault(); handleToggleActive(template); }}
                  disabled={toggling === template.id}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  title={template.isActive ? 'Deactivate template' : 'Activate template'}
                >
                  {toggling === template.id ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400" />
                  ) : template.isActive ? (
                    <EyeSlashIcon className="h-4 w-4" />
                  ) : (
                    <EyeIcon className="h-4 w-4" />
                  )}
                </button>

                {/* Delete */}
                <button
                  onClick={(e) => { e.preventDefault(); setDeleteTarget(template); }}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete template"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {templates.length === 0 && !error && !loading && (
        <Card padding="lg">
          <div className="text-center py-12">
            <DocumentDuplicateIcon className="h-12 w-12 text-gray-400 mx-auto" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No Templates Found</h3>
            <p className="mt-2 text-sm text-gray-500">
              {search || typeFilter !== 'all' || statusFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Upload your first PDF document template to get started'}
            </p>
            {!search && typeFilter === 'all' && statusFilter === 'all' && (
              <button
                onClick={() => navigate('/admin/esign/templates/new')}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800"
              >
                <PlusIcon className="h-4 w-4" />
                New Template
              </button>
            )}
          </div>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {(page - 1) * PAGE_SIZE + 1} to {Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 7) {
                pageNum = i + 1;
              } else if (page <= 4) {
                pageNum = i + 1;
              } else if (page >= totalPages - 3) {
                pageNum = totalPages - 6 + i;
              } else {
                pageNum = page - 3 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg ${
                    page === pageNum
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <DeleteTemplateModal
          template={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          deleting={deleting}
        />
      )}
    </div>
  );
}
